import { useEffect, useMemo, useState } from 'react'
import { Stack } from '@common/ui'
import { com } from '@eidu/web'
import EntityTypeField from '../../pages/permission/path/EntityTypeField'
import EntityAutocomplete from './EntityAutocomplete'
import usePaginatedEntitiesOfType from '../../io/usePaginatedEntitiesOfType'
import useEntityById from '../../io/useEntityById'
import EqualityHashMap from '../../util/EqualityHashMap'
import useEntityTypes, { useSortedEntityTypes } from '../../io/useEntityTypes'
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityType = com.eidu.entity.type.EntityType
import EntityId = com.eidu.entity.EntityId
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind

type EntityPickerProps = {
  value: EntityId | undefined
  onChange: (id: EntityId | undefined) => void
  kindFilter?: (kind: EntityTypeKind) => boolean
  typeLabel?: string
  entityLabel?: string
  noOptionsText?: string
}

const EntityPicker = (props: EntityPickerProps) => {
  const { kindFilter } = props
  const { entityTypes: allTypes } = useEntityTypes()
  const sortedTypes = useSortedEntityTypes()

  const allowedTypes = useMemo(
    () => (kindFilter && sortedTypes ? sortedTypes.filter((t) => kindFilter(t.kind)) : sortedTypes) ?? [],
    [sortedTypes, kindFilter]
  )

  if (!allTypes || allowedTypes.length === 0) return null

  return <EntityPickerInner {...props} allTypes={allTypes} allowedTypes={allowedTypes} />
}

type EntityPickerInnerProps = EntityPickerProps & {
  allTypes: ReadonlyMap<EntityTypeId, EntityType>
  allowedTypes: EntityType[]
}

const EntityPickerInner = ({
  value,
  onChange,
  allTypes,
  allowedTypes,
  typeLabel = 'Entity type',
  entityLabel = 'Entity',
  noOptionsText,
}: EntityPickerInnerProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTypeId, setSelectedTypeId] = useState<EntityTypeId>(allowedTypes[0].id)

  const { entity: selectedEntity } = useEntityById(value)
  useEffect(() => {
    if (selectedEntity) {
      const matchesAllowed = allowedTypes.some((t) => t.id.equals(selectedEntity.entity.typeId))
      if (matchesAllowed) {
        setSelectedTypeId(selectedEntity.entity.typeId)
      }
    }
  }, [selectedEntity, allowedTypes])

  const {
    entities,
    loadMore,
    hasMore,
    loading: entitiesLoading,
  } = usePaginatedEntitiesOfType({
    typeId: selectedTypeId,
    searchQuery,
    allTypes,
  })

  const entitiesById = useMemo(() => {
    const map = new EqualityHashMap(entities.map((e) => [e.entity.id, e]))
    if (selectedEntity) {
      map.set(selectedEntity.entity.id, selectedEntity)
    }
    return map
  }, [entities, selectedEntity])

  return (
    <Stack gap="md">
      <EntityTypeField
        entityTypeId={selectedTypeId}
        types={allowedTypes}
        onChange={(type) => {
          setSelectedTypeId(type.id)
          setSearchQuery('')
          onChange(undefined)
        }}
        label={typeLabel}
      />
      <EntityAutocomplete
        entitiesById={entitiesById}
        entityTypesById={allTypes}
        value={value}
        onChange={onChange}
        onSearchChange={setSearchQuery}
        loadMore={loadMore}
        hasMore={hasMore}
        loading={entitiesLoading}
        label={entityLabel}
        noOptionsText={noOptionsText}
      />
    </Stack>
  )
}

export default EntityPicker
